import { memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Download,
  TrendingUp,
  DollarSign,
  FileText,
  CheckCircle2,
  Brain,
  Zap,
  Target,
} from 'lucide-react';
import { Button } from '@/src/components/ui';

interface EconomicImpactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function EconomicImpactModal({ isOpen, onClose }: EconomicImpactModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />

          {/* Modal Content - Fixed height with scroll */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-3xl max-h-[90vh] bg-[#0a0a0a] border border-white/10 rounded-xl shadow-2xl flex flex-col"
          >
            {/* Close button - sticky */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 z-50 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
              aria-label="Cerrar modal"
            >
              <X size={18} />
            </button>

            {/* Scrollable content */}
            <div className="overflow-y-auto flex-1 overscroll-contain">
              {/* Header */}
              <div className="p-5 md:p-6 bg-gradient-to-br from-indigo-900/30 to-purple-900/30 border-b border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <FileText size={14} className="text-indigo-400" />
                  <span className="text-xs font-bold text-indigo-400 uppercase tracking-widest">
                    Estudio Técnico
                  </span>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-white mb-2 pr-8">
                  Reasoning Agentic RAG
                </h2>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Evolución de RAG: de flujos estáticos a agentes con razonamiento.
                </p>
              </div>

              {/* Content */}
              <div className="p-5 md:p-6 space-y-5">
                {/* Key Metrics */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-[#111] rounded-lg border border-white/5">
                    <div className="flex items-center gap-1.5 mb-1 text-green-400">
                      <TrendingUp size={14} />
                      <span className="text-[10px] font-bold uppercase">FRT</span>
                    </div>
                    <div className="text-2xl font-bold text-white">95%</div>
                    <p className="text-gray-500 text-[10px]">Reducción</p>
                  </div>
                  <div className="p-3 bg-[#111] rounded-lg border border-white/5">
                    <div className="flex items-center gap-1.5 mb-1 text-blue-400">
                      <Target size={14} />
                      <span className="text-[10px] font-bold uppercase">Precisión</span>
                    </div>
                    <div className="text-2xl font-bold text-white">+40%</div>
                    <p className="text-gray-500 text-[10px]">vs tradicional</p>
                  </div>
                  <div className="p-3 bg-[#111] rounded-lg border border-white/5">
                    <div className="flex items-center gap-1.5 mb-1 text-purple-400">
                      <DollarSign size={14} />
                      <span className="text-[10px] font-bold uppercase">ROI</span>
                    </div>
                    <div className="text-2xl font-bold text-white">2.3x</div>
                    <p className="text-gray-500 text-[10px]">6 meses</p>
                  </div>
                </div>

                {/* Problem Statement */}
                <div>
                  <h3 className="text-base font-bold text-white mb-2 flex items-center gap-2">
                    <Zap size={16} className="text-yellow-400" />
                    El Problema
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    RAG básico falla en síntesis multi-documento. Solución: integrar razonamiento.
                  </p>
                </div>

                {/* Two Systems */}
                <div>
                  <h3 className="text-base font-bold text-white mb-3 flex items-center gap-2">
                    <Brain size={16} className="text-indigo-400" />
                    Sistema 1 vs Sistema 2
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-blue-500/5 rounded-lg border border-blue-500/20">
                      <h4 className="font-bold text-blue-300 text-sm mb-1">Sistema 1</h4>
                      <p className="text-gray-400 text-xs mb-2">Rápido, reglas fijas</p>
                      <ul className="space-y-1.5 text-xs text-gray-300">
                        <li className="flex items-start gap-1.5">
                          <CheckCircle2 size={12} className="text-blue-400 mt-0.5 flex-shrink-0" />
                          Flujos modulares
                        </li>
                        <li className="flex items-start gap-1.5">
                          <CheckCircle2 size={12} className="text-blue-400 mt-0.5 flex-shrink-0" />
                          Predecible
                        </li>
                      </ul>
                    </div>
                    <div className="p-3 bg-indigo-500/5 rounded-lg border border-indigo-500/20">
                      <h4 className="font-bold text-indigo-300 text-sm mb-1">Sistema 2</h4>
                      <p className="text-gray-400 text-xs mb-2">Analítico, autónomo</p>
                      <ul className="space-y-1.5 text-xs text-gray-300">
                        <li className="flex items-start gap-1.5">
                          <CheckCircle2
                            size={12}
                            className="text-indigo-400 mt-0.5 flex-shrink-0"
                          />
                          Decisiones LLM
                        </li>
                        <li className="flex items-start gap-1.5">
                          <CheckCircle2
                            size={12}
                            className="text-indigo-400 mt-0.5 flex-shrink-0"
                          />
                          Adaptable
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Key Architectures */}
                <div>
                  <h3 className="text-base font-bold text-white mb-3">Arquitecturas</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { name: 'Self-RAG', desc: 'Auto-corrección' },
                      { name: 'CRAG', desc: 'Eval. calidad' },
                      { name: 'RAPTOR', desc: 'Resúmenes' },
                      { name: 'ReAct', desc: 'Pensamiento→Acción' },
                    ].map((arch, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 p-2 bg-white/5 rounded text-xs"
                      >
                        <span className="text-indigo-400 font-mono font-bold">{arch.name}</span>
                        <span className="text-gray-400">{arch.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Conclusion */}
                <div className="p-4 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-lg border border-indigo-500/20">
                  <h3 className="font-bold text-white text-sm mb-1">Conclusión</h3>
                  <p className="text-gray-300 text-xs leading-relaxed">
                    Agentic RAG es el futuro. En IntelliGrow implementamos estas arquitecturas para
                    asistentes que razonan.
                  </p>
                </div>

                {/* CTA */}
                <div className="flex gap-3 pt-2">
                  <a
                    href="/docs/estudio_arxiv_rag.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button
                      className="w-full text-sm"
                      icon={<Download size={16} />}
                      iconPosition="left"
                    >
                      Descargar PDF
                    </Button>
                  </a>
                  <Button variant="outline" className="flex-1 text-sm" onClick={onClose}>
                    Cerrar
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export default memo(EconomicImpactModal);
